
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PEOVerification } from '@/types/peo.types';
import { useState } from 'react';
import { toast } from 'sonner';

export const useVerifications = (isSuperAdmin: boolean) => {
  const [processingId, setProcessingId] = useState<string | null>(null);

  const { data: verifications, isLoading, error, refetch } = useQuery({
    queryKey: ['peo-verifications'],
    queryFn: async () => {
      try {
        if (!isSuperAdmin) {
          console.error('Access denied: Non-super admin attempting to access verifications');
          throw new Error("Access denied: Only super admins can view this page");
        }
        
        console.log('Fetching verifications with isSuperAdmin:', isSuperAdmin);
        
        const { data: verificationData, error: verificationError } = await supabase
          .from('peo_verifications')
          .select('id, user_id, status, created_at, updated_at, approved_by, approved_at, rejection_reason')
          .eq('status', 'pending');
        
        console.log('Verification Data:', verificationData);
        
        if (verificationError) {
          console.error('Verification query comprehensive error:', {
            message: verificationError.message,
            details: verificationError,
            code: verificationError.code
          });
          throw verificationError;
        }
        
        const verificationResults: PEOVerification[] = [];
        
        for (const verification of verificationData) {
          console.log('Processing verification:', verification);
          
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('email, first_name, last_name')
            .eq('id', verification.user_id)
            .maybeSingle();
          
          if (profileError) {
            console.error('Comprehensive profile query error:', {
              userId: verification.user_id,
              message: profileError.message,
              details: profileError,
              code: profileError.code
            });
          }
          
          verificationResults.push({
            ...verification,
            profile: profileError ? undefined : profileData || undefined
          });
        }
        
        console.log('Final verification results:', verificationResults);
        return verificationResults;
      } catch (error) {
        console.error('Comprehensive error in verification fetching:', {
          message: error instanceof Error ? error.message : 'Unknown error',
          error: error
        });
        throw error;
      }
    },
    enabled: isSuperAdmin
  });

  const handleVerification = async (userId: string, status: 'approved' | 'rejected', rejectionReason?: string) => {
    try {
      setProcessingId(userId);
      
      console.log('Handling verification:', { userId, status, rejectionReason });
      
      // First, get the current user's profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('email, first_name, last_name')
        .eq('id', userId)
        .single();
        
      if (profileError) {
        console.error('Error fetching profile for verification:', profileError);
        throw profileError;
      }
      
      // Update the verification status
      const { error: updateError } = await supabase
        .from('peo_verifications')
        .update({
          status,
          approved_by: (await supabase.auth.getUser()).data.user?.id,
          approved_at: status === 'approved' ? new Date().toISOString() : null,
          rejection_reason: rejectionReason
        })
        .eq('user_id', userId);

      if (updateError) {
        console.error('Comprehensive update error:', {
          message: updateError.message,
          details: updateError,
          code: updateError.code
        });
        throw updateError;
      }

      // If the verification is approved, check if the user already has a PEO provider record
      if (status === 'approved') {
        console.log('Creating PEO provider for approved user:', profileData);
        
        // First check if the user is already linked to a provider
        const { data: existingRep, error: repError } = await supabase
          .from('peo_representatives')
          .select('provider_id')
          .eq('user_id', userId)
          .maybeSingle();
        
        if (repError) {
          console.error('Error checking for existing representative link:', repError);
        }
        
        // If user is already linked to a provider, update the verification status and exit
        if (existingRep && existingRep.provider_id) {
          console.log('User is already linked to a provider:', existingRep.provider_id);
          
          // Use the new security definer function to update the verification status
          const { data: linkSuccess, error: linkError } = await supabase
            .rpc('link_user_to_provider', {
              p_user_id: userId,
              p_provider_id: existingRep.provider_id,
              p_verification_status: 'verified'
            });
            
          if (linkError) {
            console.error('Error updating representative verification status:', linkError);
            toast.error('Failed to update representative verification status');
          } else {
            toast.success('PEO provider verification updated successfully');
          }
          
          await refetch();
          return;
        }
        
        // Create a clean name from first_name and last_name, defaulting if empty
        const providerName = `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() || 'New PEO Provider';
        
        // Use RPC function to create a PEO provider (this bypasses RLS)
        const { data: providerData, error: providerError } = await supabase
          .rpc('create_peo_provider', {
            p_name: providerName,
            p_contact_email: profileData.email,
            p_employee_range: '1-99',
            p_specialties: [],
            p_states_served: [],
            p_rating: 3.0,
            p_years_in_business: 1,
            p_is_esac_accredited: false
          });

        if (providerError) {
          console.error('Error creating PEO provider using RPC:', providerError);
          toast.error('Failed to create PEO provider record');
          throw providerError;
        }

        // providerData should be a UUID string or an array of strings.
        let providerId: string | null = null;
        if (Array.isArray(providerData) && providerData.length > 0) {
          // Sometimes PostgREST returns an array with one value
          providerId = providerData[0];
        } else if (typeof providerData === 'string') {
          providerId = providerData;
        } else {
          providerId = null;
        }

        if (!providerId) {
          toast.error('Could not determine new provider ID');
          throw new Error('Provider ID not returned from RPC');
        }
        
        // Use the new security definer function to link the user to the provider
        const { data: linkResult, error: linkError } = await supabase
          .rpc('link_user_to_provider', {
            p_user_id: userId, 
            p_provider_id: providerId,
            p_verification_status: 'verified'
          });

        if (linkError) {
          console.error('Error linking user to provider:', linkError);
          toast.error('Failed to link user to PEO provider');
          throw linkError;
        }
        
        if (!linkResult) {
          console.error('Failed to link user to provider, function returned false');
          toast.error('Failed to link user to PEO provider');
        } else {
          console.log('Successfully linked user to PEO provider');
          toast.success('PEO provider approved successfully');
        }
      } else {
        toast.success('PEO provider rejected successfully');
      }

      await refetch();
    } catch (error) {
      console.error('Error processing verification:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        error: error
      });
      toast.error('Failed to process verification');
    } finally {
      setProcessingId(null);
    }
  };

  return {
    verifications,
    isLoading,
    error,
    refetch,
    processingId,
    handleVerification
  };
};
